<script setup lang="ts">
import { onMounted, ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LifeDTO } from '@/composables/life/life'
import { handleErr } from '@/composables/exception/handler'
import type { CommonResponse } from '@/apis/api'
import type { Ref } from 'vue'
import { CODE_OK } from '@/apis/code'
import { ElMessage } from 'element-plus'
import { renderMessage } from '@/utils/util'
import { titleBar } from '@/composables/menu/title'
import { sanitizeForSerialization } from '@/utils/serialization'
import {
  listFavoriteLife,
  type ListFavoriteLifeCommand,
  type ListFavoriteLifeResponse,
  type SearchFavoriteLifeCommand,
  type SearchOpenLifeResponse,
  searchOpenLife
} from '@/composables/memorize/memorize'

const { t } = useI18n()
const route = useRoute()
const requestURL = useRequestURL()

const canonicalUrl = computed(() => `${requestURL.origin}${route.path}`)
const pageTitle = computed(() => t('open_life.page_title'))
const pageDescription = computed(() => t('open_life.tip'))

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

// --- State ---
const openLifeList = ref<LifeDTO[]>([])
const lastLifeId = ref<string | null>(null)
const pageSize = ref(50)
const page = ref(1)
const hasMoreItems = ref(true)
const loading = ref(false)
const userNum = ref(0)
const lifeNum = ref(0)

const searchName = ref('')
const isSearch = ref(false)
const searchResult = ref<LifeDTO[]>([])

const { data: ssrData } = await useAsyncData('open-life-list', async () => {
  const { data } = await listFavoriteLife({
    lifeId: null,
    page: 1,
    pageSize: pageSize.value,
    sort: null
  })
  return data.value ? sanitizeForSerialization(data.value) : null
})

if (ssrData.value && ssrData.value.code === CODE_OK) {
  const response = ssrData.value.data
  if (response && Array.isArray(response.items)) {
    openLifeList.value = response.items
    userNum.value = Number(response.userCount) || 0
    lifeNum.value = Number(response.memorialCount) || 0

    if (response.items.length > 0) {
      lastLifeId.value = response.items[response.items.length - 1].id
      page.value = 2
    }

    if (response.items.length === 0) {
      hasMoreItems.value = false
    }
  }
}

// --- Computed ---
const bottomTips = computed(() => {
  if (loading.value) {
    return t('alt.name')
  } else if (openLifeList.value.length === 0 && searchResult.value.length === 0) {
    return t('message.no_data')
  } else if (hasMoreItems.value) {
    return t('open_life.load_more')
  } else {
    return t('open_life.no_more')
  }
})

// --- Methods ---
const checkScrollBottom = () => {
  if ((openLifeList.value.length > 0 || searchResult.value.length > 0) && !loading.value && hasMoreItems.value) {
    const scrollHeight = document.documentElement.scrollHeight
    const scrollTop = document.documentElement.scrollTop
    const clientHeight = document.documentElement.clientHeight

    if (scrollHeight - scrollTop - clientHeight < 150) {
      if (isSearch.value) {
        searchNewPage()
      } else {
        loadLifeListData()
      }
    }
  }
}

async function loadLifeListData() {
  if (loading.value || !hasMoreItems.value) {
    return
  }
  loading.value = true
  const command: ListFavoriteLifeCommand = {
    lifeId: lastLifeId.value,
    page: page.value,
    pageSize: pageSize.value,
    sort: null
  }
  const { data, error, statusCode } = await listFavoriteLife(command)
  if (error.value) {
    handleErr(data, error, statusCode)
    loading.value = false
  } else {
    const resData = data as Ref<CommonResponse<ListFavoriteLifeResponse>>
    if (resData.value && resData.value.code === CODE_OK) {
      loading.value = false
      // Update stats just in case
      userNum.value = Number(resData.value.data.userCount) || 0
      lifeNum.value = Number(resData.value.data.memorialCount) || 0

      if (resData.value.data.items && resData.value.data.items.length > 0) {
        openLifeList.value.push(...resData.value.data.items)
        lastLifeId.value = resData.value.data.items[resData.value.data.items.length - 1].id
        page.value++
      }

      if (resData.value.data.items.length === 0) {
        hasMoreItems.value = false
      } else {
        hasMoreItems.value = true
      }
    } else {
      loading.value = false
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

async function searchNewPage() {
  if (loading.value) return
  loading.value = true

  const command: SearchFavoriteLifeCommand = {
    name: searchName.value ? searchName.value : null,
    lifeId: lastLifeId.value,
    page: page.value,
    pageSize: pageSize.value
  }
  const { data, error, statusCode } = await searchOpenLife(command)
  if (error.value) {
    loading.value = false
    handleErr(data, error, statusCode)
  } else {
    loading.value = false
    const resData = data as Ref<CommonResponse<SearchOpenLifeResponse>>
    if (resData.value && resData.value.code === CODE_OK) {
      if (resData.value.data.items) {
        searchResult.value.push(...resData.value.data.items)
        if (resData.value.data.items.length === 0) {
          hasMoreItems.value = false
        } else {
          hasMoreItems.value = true
          if (resData.value.data.items.length > 0) {
            lastLifeId.value = resData.value.data.items[resData.value.data.items.length - 1].id
            page.value++
          }
        }
      }
    } else {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
    }
  }
}

async function search() {
  if (loading.value) return

  isSearch.value = true
  page.value = 1
  openLifeList.value = []
  searchResult.value = []
  lastLifeId.value = null
  hasMoreItems.value = true
  loading.value = false

  const command: SearchFavoriteLifeCommand = {
    name: searchName.value ? searchName.value : null,
    lifeId: lastLifeId.value,
    page: page.value,
    pageSize: pageSize.value
  }
  loading.value = true
  const { data, error, statusCode } = await searchOpenLife(command)
  if (error.value) {
    loading.value = false
    handleErr(data, error, statusCode)
  } else {
    loading.value = false
    const resData = data as Ref<CommonResponse<SearchOpenLifeResponse>>
    if (resData.value && resData.value.code === CODE_OK) {
      if (resData.value.data.items) {
        searchResult.value = resData.value.data.items
        if (resData.value.data.items.length === 0) {
          hasMoreItems.value = false
        } else {
          hasMoreItems.value = true
          if (resData.value.data.items.length > 0) {
            lastLifeId.value = resData.value.data.items[resData.value.data.items.length - 1].id
            page.value++
          }
        }
      }
    } else {
      loading.value = false
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
      hasMoreItems.value = false
    }
  }
}

watch(searchName, () => {
  if (isSearch.value && searchName.value === '') {
    isSearch.value = false
    page.value = 1
    openLifeList.value = []
    searchResult.value = []
    lastLifeId.value = null
    hasMoreItems.value = true
    loading.value = false

    loadLifeListData()
  }
})

// --- Lifecycle ---
onMounted(() => {
  titleBar.value.transparent = false
  titleBar.value.title = t('open_life.page_title')
  titleBar.value.backable = true
  titleBar.value.hideBack = false
  titleBar.value.menus = []
  titleBar.value.life = null
  titleBar.value.lifeId = null

  // If SSR failed or we have no data (and not searching), try loading
  // But SSR should have loaded it.
  if (openLifeList.value.length === 0 && !isSearch.value) {
    loadLifeListData()
  }

  window.addEventListener('scroll', checkScrollBottom)
})

onUnmounted(() => {
  window.removeEventListener('scroll', checkScrollBottom)
})
</script>

<template>
  <BaseLayout px-6 mt-10 md:mt-0 w-full mx-auto>
    <div w-full sticky top-0 md:top-10 z-10 mx-auto bg-base py-4>
      <div relative w-full>
        <input
          type="text"
          v-model="searchName"
          :placeholder="t('open_life.search_placeholder')"
          input-base
          rounded-full
          relative
          text-base
          w-full
          pr-10 />
        <div
          class="i-material-symbols:search h-7 w-7 absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
          style="color: #b2b2b2"
          @click="search()" />
      </div>
    </div>
    <div v-show="userNum > 0" mt-2 text-sm text-third mx-auto mb-6>
      {{
        isSearch
          ? t('open_life.tip')
          : t('open_life.stat', {
              userNum: userNum,
              lifeNum: lifeNum
            })
      }}
    </div>

    <div grid grid-cols-1 gap-4 w-full>
      <div v-for="(life, index) in isSearch ? searchResult : openLifeList" :key="life.id" w-full>
        <OpenLifeItem :lifeInfo="life" :showCreator="true" :searchKey="isSearch ? searchName : ''" cursor-pointer />
      </div>
    </div>

    <div w-full text-center text-base text-third mx-auto pb-7 pt-4>
      {{ bottomTips }}
    </div>
  </BaseLayout>
</template>
